import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { db } from "../firebase"; // Adjust path based on your firebase config location
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase"; // Adjust path based on your firebase config location
import { sendServicesInquiryEmail } from "../data/emailForServices"; // Import the new email service

const Services = () => {
  const { t } = useTranslation();
  const [user] = useAuthState(auth);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [userDetails, setUserDetails] = useState({ name: "", pan: "", phone: "", city: "", email: "" });
  const [formData, setFormData] = useState({
    individual: {
      selectedServices: [],
      message: ""
    },
    packages: {
      selectedPackages: [],
      message: ""
    }
  });

  // Individual Services Data
  const individualServices = [
    "INDIVIDUAL PAN COMPLIANCE",
    "INDIVIDUAL AADHAR - UIDAI COMPLIANCE",
    "EFILING REGISTRATION & PROFILE WORK",
    "NIL RETURN",
    "ANNUAL ITR 1 & 2 FILING",
    "ANNUAL ITR FILING (Police & Army)",
    "ANNUAL ITR FILING (Police & Army)",
    "ITR 3",
    "ITR 4 - BUSINESS RETURN",
    "COMPUTATION OF INCOME",
    "ANNUAL FORM 16",
    "ITR PORTAL ACCESS - ANUALLY",
    "SHARES CALCULATION",
    "UPDATED RETURN",
    "REVISE RETURN",
    "TRACES COMPLIANCES",
    "TDS ON SALARY",
    "TDS ON PROPERTY",
    "OTHER TDS PAYMENT & CHALLAN",
    "ITR NOTICES",
    "COMPLIANCE WORK - OTHER"
  ];

  // Packages Data with Sub-services
  const packagesData = {
    "BUSINESS REGISTRATION PACKAGES": [
      "COUNSELLING & CONSULTANCY",
      "SHOP ACT",
      "UDYAM REGISTRATION CERTIFICATE",
      "BUSINESS STAMP",
      "BANKING ASSISTANCE AND SERVICES",
      "LETTERHEAD DRAFTING",
      "GST REGISTRATION",
      "E FILING REGISTRATION"
    ],
    "STARTUP COMPANY REGISTRATION": [
      "COUNSELLING & CONSULTANCY",
      "SHOP ACT",
      "UDYAM REGISTRATION CERTIFICATE",
      "BUSINESS STAMP",
      "BANKING ASSISTANCE AND SERVICES",
      "LETTERHEAD DRAFTING",
      "PRIVATE LIMITED INCORPORATION",
      "START - UP INCUBATION",
      "ISO CERTIFICATION",
      "BUSINESS PLAN & PROJECT REPORT",
      "DIRECTORS DSC CLASS 3 - 2 YEARS"
    ],
    "PRIVATE LIMITED INCORPORATION": [
      "COUNSELLING & CONSULTANCY",
      "DIRECTORS DSC CLASS 3 - 2 YEARS",
      "DIRECTORS DIN",
      "NAME VERIFICATION",
      "INCORPORATION DOCUMENTS",
      "COMPANY PAN",
      "TAN REGISTRATION",
      "MOA & AOA",
      "BANKING SERVICES",
      "AUDITOR APPOINTMENT",
      "SHARE CERTIFICATE",
      "LETTER HEAD DRAFTING"
    ],
    "MONTHLY BUSINESS SERVICES": [
      "MONTHLY ACCOUNTING SERVICES",
      "MONTHLY GST FILING (Proprietor)",
      "MONTHLY GST FILING (COMPANIES)",
      "EPFO PAYMENTS & CALCULATION",
      "ESIC FILING",
      "PT RETURNS",
      "TDS RETURNS & CALCULATION",
      "SALARY & PAYROLL WORKING",
      "MONTHLY BUDGETING & FINANCE"
    ]
  };

  // Fetch user details from Firebase
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user) {
        try {
          // Extract PAN from email (same as useUser.js)
          const match = user.email.match(/\+(.*?)@/);
          const pan = match && match[1] ? match[1].toUpperCase() : "";
          if (!pan) {
            setUserDetails({ name: "", pan: "", phone: "", city: "", email: user.email || "" });
            return;
          }

          // Fetch user data from usersTable using PAN
          const userDoc = await getDoc(doc(db, "usersTable", pan));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserDetails({
              name: userData.name || "",
              pan: pan,
              phone: userData.phone || "",
              city: userData.city || "",
              email: user.email || ""
            });
          } else {
            setUserDetails({ 
              name: "", 
              pan: pan, 
              phone: "", 
              city: "", 
              email: user.email || "" 
            });
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
          setUserDetails({ name: "", pan: "", phone: "", city: "", email: user.email || "" });
        }
      }
    };

    fetchUserDetails();
  }, [user]);

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const handleServiceChange = (service, checked) => {
    setFormData(prev => ({
      ...prev,
      individual: {
        ...prev.individual,
        selectedServices: checked
          ? [...prev.individual.selectedServices, service]
          : prev.individual.selectedServices.filter(s => s !== service)
      }
    }));
  };

  const handlePackageChange = (packageName, checked) => {
    setFormData(prev => ({
      ...prev,
      packages: {
        ...prev.packages,
        selectedPackages: checked
          ? [...prev.packages.selectedPackages, packageName]
          : prev.packages.selectedPackages.filter(p => p !== packageName)
      }
    }));
  };

  const handleMessageChange = (type, message) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        message
      }
    }));
  };

  const handleSubmit = async (type) => {
    if (!user) {
      alert("Please login to submit inquiry");
      return;
    }

    const currentData = formData[type];
    const selectedItems = type === "individual" ? currentData.selectedServices : currentData.selectedPackages;

    if (selectedItems.length === 0) {
      alert(`Please select at least one ${type === "individual" ? "service" : "package"}`);
      return;
    }

    try {
      const inquiryData = {
        userId: user.uid,
        userEmail: user.email,
        name: userDetails.name,
        pan: userDetails.pan,
        type: type,
        selectedItems: selectedItems,
        message: currentData.message,
        timestamp: new Date(),
        status: "pending"
      };

      if (type === "packages") {
        // Add sub-services for selected packages
        inquiryData.packageDetails = {};
        selectedItems.forEach(packageName => {
          inquiryData.packageDetails[packageName] = packagesData[packageName];
        });
      }

      // Save to Firebase
      await addDoc(collection(db, "submitinquiry"), inquiryData);

      // Send email notification
      const emailResult = await sendServicesInquiryEmail(userDetails, inquiryData);
      
      if (emailResult.success) {
        console.log(`Email sent successfully to ${emailResult.team}:`, emailResult.recipients);
      } else {
        console.error("Failed to send email:", emailResult.error);
        // Don"t show email error to user, just log it
      }

      alert(`${type === "individual" ? "Individual Services" : "Package"} inquiry submitted successfully!`);

      // Reset form
      setFormData(prev => ({
        ...prev,
        [type]: {
          selectedServices: [],
          selectedPackages: [],
          message: ""
        }
      }));

    } catch (error) {
      console.error("Error submitting inquiry:", error);
      alert("Error submitting inquiry. Please try again.");
    }
  };

  const accordionStyle = {
    marginBottom: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
  };

  const headerStyle = (isActive) => ({
    padding: "20px",
    backgroundColor: isActive ? "#007bff" : "#f8f9fa",
    color: isActive ? "white" : "#333",
    cursor: "pointer",
    borderBottom: isActive ? "1px solid #ddd" : "none",
    borderRadius: isActive ? "8px 8px 0 0" : "8px",
    transition: "all 0.3s ease",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  });

  const contentStyle = {
    padding: "25px",
    backgroundColor: "#fff"
  };

  const checkboxStyle = {
    marginBottom: "12px",
    display: "flex",
    alignItems: "center"
  };

  const formStyle = {
    marginTop: "25px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px"
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    boxSizing: "border-box"
  };

  const buttonStyle = {
    padding: "12px 30px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    transition: "background-color 0.3s ease"
  };

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto", padding: "20px" }}>
      <h2 style={{ textAlign: "center", marginBottom: "40px", color: "#333" }}>
        Our Services
      </h2>

      {/* Individual Services Accordion */}
      <div style={accordionStyle}>
        <div
          style={headerStyle(activeAccordion === 0)}
          onClick={() => toggleAccordion(0)}
        >
          <h3 style={{ margin: 0 }}>Individual Services</h3>
          <span style={{ fontSize: "20px" }}>
            {activeAccordion === 0 ? "−" : "+"}
          </span>
        </div>
        {activeAccordion === 0 && (
          <div style={contentStyle}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "15px" }}>
              {individualServices.map((service, index) => (
                <div key={index} style={checkboxStyle}>
                  <input
                    type="checkbox"
                    id={`service-${index}`}
                    checked={formData.individual.selectedServices.includes(service)}
                    onChange={(e) => handleServiceChange(service, e.target.checked)}
                    style={{ marginRight: "10px", transform: "scale(1.2)" }}
                  />
                  <label htmlFor={`service-${index}`} style={{ fontSize: "14px", cursor: "pointer" }}>
                    {service}
                  </label>
                </div>
              ))}
            </div>

            {formData.individual.selectedServices.length > 0 && (
              <div style={formStyle}>
                <h4 style={{ marginTop: 0, color: "#333" }}>Inquiry Form</h4>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={userDetails.name}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: "#e9ecef" }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    PAN
                  </label>
                  <input
                    type="text"
                    value={userDetails.pan}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: "#e9ecef" }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    Selected Services
                  </label>
                  <div style={{
                    padding: "12px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "6px",
                    minHeight: "60px"
                  }}>
                    {formData.individual.selectedServices.map((service, index) => (
                      <span key={index} style={{
                        display: "inline-block",
                        margin: "3px",
                        padding: "4px 8px",
                        backgroundColor: "#007bff",
                        color: "white",
                        borderRadius: "4px",
                        fontSize: "12px"
                      }}>
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    Message
                  </label>
                  <textarea
                    value={formData.individual.message}
                    onChange={(e) => handleMessageChange("individual", e.target.value)}
                    placeholder="Please provide any additional details or requirements..."
                    style={{
                      ...inputStyle,
                      minHeight: "100px",
                      resize: "vertical"
                    }}
                  />
                </div>

                <button
                  onClick={() => handleSubmit("individual")}
                  style={buttonStyle}
                  onMouseOver={(e) => e.target.style.backgroundColor = "#218838"}
                  onMouseOut={(e) => e.target.style.backgroundColor = "#28a745"}
                >
                  Submit Inquiry
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Packages Accordion */}
      <div style={accordionStyle}>
        <div
          style={headerStyle(activeAccordion === 1)}
          onClick={() => toggleAccordion(1)}
        >
          <h3 style={{ margin: 0 }}>Packages</h3>
          <span style={{ fontSize: "20px" }}>
            {activeAccordion === 1 ? "−" : "+"}
          </span>
        </div>
        {activeAccordion === 1 && (
          <div style={contentStyle}>
            {Object.entries(packagesData).map(([packageName, subServices], packageIndex) => (
              <div key={packageIndex} style={{ marginBottom: "25px", border: "1px solid #e9ecef", borderRadius: "6px", padding: "15px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                  <input
                    type="checkbox"
                    id={`package-${packageIndex}`}
                    checked={formData.packages.selectedPackages.includes(packageName)}
                    onChange={(e) => handlePackageChange(packageName, e.target.checked)}
                    style={{ marginRight: "12px", transform: "scale(1.3)" }}
                  />
                  <label htmlFor={`package-${packageIndex}`} style={{ fontSize: "16px", fontWeight: "bold", cursor: "pointer", color: "#333" }}>
                    {packageName}
                  </label>
                </div>

                <div style={{ marginLeft: "30px" }}>
                  <h5 style={{ marginBottom: "10px", color: "#666" }}>Included Services:</h5>
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {subServices.map((subService, subIndex) => (
                      <li key={subIndex} style={{ marginBottom: "5px", fontSize: "14px", color: "#555" }}>
                        {subService}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}

            {formData.packages.selectedPackages.length > 0 && (
              <div style={formStyle}>
                <h4 style={{ marginTop: 0, color: "#333" }}>Package Inquiry Form</h4>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={userDetails.name}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: "#e9ecef" }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    PAN
                  </label>
                  <input
                    type="text"
                    value={userDetails.pan}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: "#e9ecef" }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    Selected Packages
                  </label>
                  <div style={{
                    padding: "12px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "6px",
                    minHeight: "60px"
                  }}>
                    {formData.packages.selectedPackages.map((packageName, index) => (
                      <span key={index} style={{
                        display: "inline-block",
                        margin: "3px",
                        padding: "6px 12px",
                        backgroundColor: "#28a745",
                        color: "white",
                        borderRadius: "4px",
                        fontSize: "13px"
                      }}>
                        {packageName}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                    Message
                  </label>
                  <textarea
                    value={formData.packages.message}
                    onChange={(e) => handleMessageChange("packages", e.target.value)}
                    placeholder="Please provide any additional details or requirements for the selected packages..."
                    style={{
                      ...inputStyle,
                      minHeight: "100px",
                      resize: "vertical"
                    }}
                  />
                </div>

                <button
                  onClick={() => handleSubmit("packages")}
                  style={buttonStyle}
                  onMouseOver={(e) => e.target.style.backgroundColor = "#218838"}
                  onMouseOut={(e) => e.target.style.backgroundColor = "#28a745"}
                >
                  Submit Package Inquiry
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;